import React, { useState, useEffect } from "react";
import { useStore } from "store";
import {
  Flex,
  Space,
  Typography,
  Modal,
  Table,
  Divider,
  Tag,
  Button,
  Tooltip,
} from "antd";
import { useWindowDimensions } from "resources/BrowserManager";
import { useNavigate, useParams } from "react-router-dom";

import ApiManager from "resources/ApiManager/api";
import CONSTANTS from "shared/constants";
import {
  ArrowRightOutlined,
  FrownOutlined,
  ArrowDownOutlined,
  Loading3QuartersOutlined,
  FileUnknownOutlined,
  InfoCircleFilled,
} from "@ant-design/icons";

const { Text } = Typography;

import { getFormattedUnixDate } from "shared/utilities";
import {
  CfdiTags,
  CfdiAmount,
  CfdiFileButton,
  CfdiPaymentProgress,
  CfdiLink,
  EfosTag,
  RowItem,
} from "../../../components/Shared/CfdiElements";

const amountsStyle = {
  fontSize: "14px",
};

const SectionLinkedCfdis = ({ id_cfdi, smallView, id_cfdi_url, setItem }) => {
  const [cfdiLinkItems, setCfdiLinkItems] = useState(null);

  const getLinkedCfdis = async () => {
    const payload = {
      id_cfdi: id_cfdi,
    };

    const apiRes = await ApiManager.getUserCfdiLinks(payload.id_cfdi);

    //Silently return when error was for session expired, as the api handler will handle the message and redirection to /login
    if (apiRes.response.code === CONSTANTS.API_RESPONSE_ERROR_SESSION_EXPIRED) {
      return;
    }

    if (apiRes.response.code !== 2000) {
      setCfdiLinkItems(-1);
      return;
    }

    const { linked_cfdis } = apiRes.data;

    //Find all linked payments per CFDI
    const paymentItems = linked_cfdis.flatMap((cfdi) =>
      (cfdi.payments || [])
        .filter((payment) => payment.cfdi_link?.id_cfdi === id_cfdi)
        .map((payment) => ({
          ...cfdi,
          payment,
        })),
    );

    setCfdiLinkItems(paymentItems);
    return;
  };

  useEffect(() => {
    getLinkedCfdis();
  }, [id_cfdi]);

  //Note: only enable file buttons after fixing the BE bug where links return empty because we don't send the emitter/receiver rfc param.
  const columns = smallView
    ? [
        {
          title: "Documento",
          key: "id_cfdi_link",
          render: (_, { item }) => (
            <Flex vertical gap={8}>
              <Flex align="flex-start">
                <CfdiLink
                  id_cfdi={item.id_cfdi}
                  id_cfdi_url={id_cfdi_url}
                  setItem={setItem}
                />
              </Flex>
              <CfdiTags item={item} />
              <RowItem
                title={"Parcialidad"}
                text={
                  <Flex justify="center">
                    {
                      item.payments?.find(
                        (payment) => payment.cfdi_link?.id_cfdi === id_cfdi,
                      ).cfdi_link.partiality_number
                    }
                  </Flex>
                }
              />
              <Flex justify="flex-start">
                <CfdiAmount
                  item={
                    item.payments?.find(
                      (payment) => payment.cfdi_link?.id_cfdi === id_cfdi,
                    ).cfdi_link
                  }
                  amountType="total"
                  includeCurrency={true}
                  style={amountsStyle}
                />
              </Flex>
              {getFormattedUnixDate(item.date_created_at)}
              {/* <Flex gap={5} align="flex-end">
                <CfdiFileButton type={"pdf"} file_url={item.pdf_url} />
                <CfdiFileButton type={"xml"} file_url={item.xml_url} />
              </Flex> */}
            </Flex>
          ),
        },
      ]
    : [
        {
          title: "Documento",
          key: "id_cfdi_link",
          render: (_, { item }) => (
            <Flex vertical gap={5} align="flex-start">
              <CfdiLink
                id_cfdi={item.id_cfdi}
                id_cfdi_url={id_cfdi_url}
                setItem={setItem}
              />
            </Flex>
          ),
        },
        {
          title: "",
          key: "tags",
          render: (_, { item }) => <CfdiTags item={item} />,
        },
        {
          title: "Parcialidad",
          key: "partiality",
          render: (_, { item }) => (
            <Flex justify="center">
              {item.payment?.cfdi_link.partiality_number}
            </Flex>
          ),
        },
        {
          title: "Importe pagado",
          key: "amount_paid",
          render: (_, { item }) => (
            <Flex vertical gap={5} align="flex-end">
              <CfdiAmount
                item={item.payment?.cfdi_link}
                amountType="total"
                includeCurrency={true}
                style={amountsStyle}
              />
            </Flex>
          ),
        },
        {
          title: "Fecha de pago",
          key: "payment_date",
          render: (_, { item }) =>
            getFormattedUnixDate(item.payment.date_payment_at),
        },
        // {
        //   title: "",
        //   key: "file_buttons",
        //   render: (_, { item }) => (
        //     <Flex gap={5} justify="flex-end">
        //       <CfdiFileButton type={"pdf"} file_url={item.pdf_url} />
        //       <CfdiFileButton type={"xml"} file_url={item.xml_url} />
        //     </Flex>
        //   ),
        // },
      ];

  let dataSource = cfdiLinkItems?.map((item, i) => {
    return {
      key: i,
      item: item,
    };
  });

  return (
    <Flex vertical gap={10}>
      <Space>
        <Text strong style={{ fontSize: "16px" }}>
          Documentos Relacionados
        </Text>
        <Tooltip title="Lista de documentos relacionados a este CFDI (Pagos, etc.).">
          <InfoCircleFilled style={{ color: "purple", cursor: "help" }} />
        </Tooltip>
      </Space>
      {cfdiLinkItems?.length > 0 ? (
        <Flex vertical gap={10}>
          <Table
            size="small"
            columns={columns}
            dataSource={dataSource}
            locale={{ emptyText: null }}
            pagination={false}
          />
        </Flex>
      ) : cfdiLinkItems?.length === 0 ? (
        <Text style={{ fontSize: "15px", textAlign: "left" }}>
          Sin documentos relacionados.
        </Text>
      ) : cfdiLinkItems === -1 ? (
        <Flex align="center" justify="center" gap={8} vertical={smallView}>
          <FrownOutlined
            style={{
              fontSize: "20px",
              color: "#1C5676",
            }}
          />
          <Text style={{ fontSize: "15px", textAlign: "center" }}>
            Hubo un error cargando los documentos relacionados de este CFDI.
          </Text>
        </Flex>
      ) : (
        <Flex align="flex-start" gap={8} vertical>
          <Loading3QuartersOutlined
            spin
            style={{
              fontSize: "20px",
              color: "#1C5676",
            }}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default function ModalCfdiDetail({ id_cfdi, cfdiType }) {
  const navigate = useNavigate();
  const { id_cfdi: id_cfdi_url } = useParams();

  const { width } = useWindowDimensions();
  const smallView = width <= 900;
  const miniWidth = width <= 410;

  const activeRfc = useStore((state) => state.activeRfc);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [item, setItem] = useState(null);

  const getItem = async (id_cfdi) => {
    const payload = {
      id_cfdi: id_cfdi,
    };

    if (cfdiType === "cfdiEmitted") {
      payload.rfc_emitter = activeRfc;
    } else if (cfdiType === "cfdiReceived") {
      payload.rfc_receiver = activeRfc;
    } else {
      throw new Error("Invalid cfdi type.");
    }

    const apiRes = await ApiManager.getUserCfdis(
      payload.id_cfdi,
      payload.rfc_emitter,
      payload.rfc_receiver,
    );

    //Silently return when error was for session expired, as the api handler will handle the message and redirection to /login
    if (apiRes.response.code === CONSTANTS.API_RESPONSE_ERROR_SESSION_EXPIRED) {
      return;
    }

    if (apiRes.response.code !== 2000) {
      setItem(-1);
      return;
    }

    const { cfdis } = apiRes.data;

    if (cfdis.length === 1) {
      setItem(cfdis[0]);
      return;
    } else {
      setItem(-1);
      return;
    }
  };

  useEffect(() => {
    if (!id_cfdi) {
      return;
    }

    setIsModalOpen(true);
    getItem(id_cfdi);
  }, [id_cfdi]);

  const handleCancel = () => {
    setIsModalOpen(false);
    setItem(null);
    navigate("/cfdis");
  };

  const renderDescription = (item, smallView = false) => (
    <Space direction="vertical" size={0}>
      {item.description}
      <Space
        size={smallView ? 0 : 20}
        direction={smallView ? "vertical" : "horizontal"}
      >
        <Space size={3}>
          <span style={{ fontWeight: "600" }}>Código SAT:</span>{" "}
          {item.sat_key_product}
        </Space>
        {!!item.is_taxable_description && (
          <Space
            size={smallView ? 0 : 3}
            direction={smallView ? "vertical" : "horizontal"}
          >
            <span style={{ fontWeight: "600" }}>Objecto impuesto:</span>{" "}
            {item.is_taxable_description}
          </Space>
        )}
      </Space>
    </Space>
  );

  const renderTaxRow = (item) => {
    if (!!!item.taxes) {
      return null;
    }

    const TaxRow = ({ type, taxObj }) => (
      <Flex
        gap={smallView ? 0 : 20}
        style={{ marginLeft: smallView ? "20px" : "48px" }}
        vertical={smallView ? true : false}
      >
        <Flex justify="space-between" gap={8}>
          <span style={{ fontWeight: "600" }}>
            {type === "traslado" ? "Traslado" : "Retención"}
          </span>
          {taxObj.Impuesto}
        </Flex>

        <Flex justify="space-between" gap={8}>
          <span style={{ fontWeight: "600" }}>{taxObj.TipoFactor}</span>
          {taxObj.TasaOCuota ? parseFloat(taxObj.TasaOCuota).toFixed(6) : null}
        </Flex>

        <Flex justify="space-between" gap={8}>
          <span style={{ fontWeight: "600" }}>Base</span>
          <CfdiAmount
            item={taxObj.Base}
            amountType="generic"
            includeCurrency={false}
            style={{
              fontSize: "14px",
            }}
          />
        </Flex>

        <Flex justify="space-between" gap={8}>
          <span style={{ fontWeight: "600" }}>Importe</span>
          <CfdiAmount
            item={taxObj.Importe}
            amountType="generic"
            includeCurrency={false}
            style={{
              fontSize: "14px",
            }}
          />
        </Flex>
      </Flex>
    );

    const taxes = [];
    if (item.taxes.traslados) {
      item.taxes.traslados.map((traslado, i) => {
        taxes.push(
          <TaxRow key={`traslado-${i}`} type="traslado" taxObj={traslado} />,
        );
        return null;
      });
    }
    if (item.taxes.retenciones) {
      item.taxes.retenciones.map((retencion, i) => {
        taxes.push(
          <TaxRow key={`retencion-${i}`} type="retencion" taxObj={retencion} />,
        );
        return null;
      });
    }

    const taxesDivider = taxes.map((row, i) => (
      <React.Fragment key={i}>
        {!!i && <Divider dashed style={{ margin: "5px 0px" }} />}
        {row}
      </React.Fragment>
    ));

    return <Flex vertical>{taxesDivider}</Flex>;
  };

  const renderUnits = (item) => {
    return (
      <Space size={5}>
        {item.sat_key_units}
        {item.units}
      </Space>
    );
  };

  const renderSmallRow = (item) => {
    return (
      <Flex vertical gap={0}>
        <RowItem title={"Código"} text={item.part_number} hideIfEmpty />
        <RowItem title={"Clave unidad"} text={renderUnits(item)} hideIfEmpty />
        <RowItem
          title={"Descripción"}
          text={renderDescription(item, true)}
          vertical
          hideIfEmpty
        />
        <RowItem
          title={"Valor unitario"}
          text={
            <CfdiAmount
              item={item}
              amountType="unit_price"
              includeCurrency={false}
              style={amountsStyle}
              align="right"
            />
          }
          indent
          hideIfEmpty
        />
        <RowItem title={"Cantidad"} text={item.quantity.toFixed(2)} indent />
        <RowItem
          title={"Importe"}
          text={
            <CfdiAmount
              item={item}
              amountType="total"
              includeCurrency={false}
              style={amountsStyle}
              align="right"
            />
          }
          indent
          hideIfEmpty
        />
        <RowItem
          title={"Descuento"}
          text={
            <CfdiAmount
              item={item}
              amountType="discount"
              includeCurrency={false}
              style={amountsStyle}
              align="right"
            />
          }
          indent
          hideIfEmpty
        />
      </Flex>
    );
  };

  const RfcInfo = ({ rfc, name, is_efos_linked, type }) => (
    <Flex vertical style={{ maxWidth: "600px" }}>
      <Text strong>
        {name}&nbsp;&nbsp;
        {!smallView &&
        is_efos_linked &&
        ((cfdiType === "cfdiReceived" && type === "emitter") ||
          (cfdiType === "cfdiEmitted" && type === "receiver")) ? (
          <EfosTag />
        ) : null}
      </Text>
      <Text strong style={{ color: "#801794", minWidth: "130px" }}>
        {rfc}&nbsp;&nbsp;
        {smallView &&
        is_efos_linked &&
        ((cfdiType === "cfdiReceived" && type === "emitter") ||
          (cfdiType === "cfdiEmitted" && type === "receiver")) ? (
          <EfosTag />
        ) : null}
      </Text>
    </Flex>
  );

  const HeaderInfo = () => (
    <Flex
      vertical={smallView}
      justify="space-between"
      gap={smallView ? 10 : 20}
    >
      <Flex gap={miniWidth ? 10 : 20} vertical={miniWidth}>
        <RfcInfo
          rfc={item.emitter.rfc}
          name={item.emitter.name}
          is_efos_linked={item.is_efos_linked}
          type="emitter"
        />
        {miniWidth ? <ArrowDownOutlined /> : <ArrowRightOutlined />}
        <RfcInfo
          rfc={item.receiver.rfc}
          name={item.receiver.name}
          is_efos_linked={item.is_efos_linked}
          type="receiver"
        />
      </Flex>
      <Flex align="center" gap={10}>
        {/* {item.cfdi_type === "I" && (
          <CfdiPaymentProgress
            payments={item.payments_summary?.payments}
            credits={item.payments_summary?.credits}
            total={item.payments_summary?.invoice_total}
          />
        )} */}
        <CfdiAmount
          item={item}
          amountType="total"
          includeCurrency={true}
          style={{
            fontWeight: "bold",
            fontSize: "25px",
          }}
          align={smallView && "left"}
        />
      </Flex>
    </Flex>
  );

  const DetailLabel = ({ title, text }) => (
    <Space
      direction={smallView ? "vertical" : "horizontal"}
      size={smallView ? 0 : 5}
    >
      <Text strong>{title}:</Text>
      <Text>{text}</Text>
    </Space>
  );

  const DetailInfo = () => {
    let emitter_internal_id = null;
    if (item.emitter_internal_id?.series || item.emitter_internal_id?.folio) {
      emitter_internal_id = !!item.emitter_internal_id.series
        ? item.emitter_internal_id.series + "-"
        : "";
      emitter_internal_id += !!item.emitter_internal_id.folio
        ? item.emitter_internal_id.folio
        : "";
    }

    return (
      <Flex justify="space-between" vertical={smallView} gap={20}>
        <Flex vertical gap={smallView ? 8 : 0}>
          {emitter_internal_id && (
            <DetailLabel title={"Folio Emisor"} text={emitter_internal_id} />
          )}
          <DetailLabel
            title={"Folio Fiscal"}
            text={
              <CfdiLink
                id_cfdi={item.id_cfdi}
                id_cfdi_url={id_cfdi_url}
                setItem={setItem}
              />
            }
          />
          <DetailLabel
            title={"Fecha de emisión"}
            text={getFormattedUnixDate(item.date_created_at)}
          />
          <Space style={{ marginTop: "5px" }}>
            <CfdiTags item={item} />
          </Space>
          <Space style={{ marginTop: "10px" }} size={0}>
            {item.user_data?.tags?.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </Space>
        </Flex>

        <Flex gap={5} align="flex-end">
          <CfdiFileButton type={"pdf"} file_url={item.pdf_url} />
          <CfdiFileButton type={"xml"} file_url={item.xml_url} />
        </Flex>
      </Flex>
    );
  };

  const TableLineItems = () => {
    const columns = smallView
      ? [
          {
            title: "Partidas",
            key: "smallRow",
            render: (_, { item }) => renderSmallRow(item),
          },
        ]
      : [
          {
            title: "Código",
            key: "ClaveProdServ",
            render: (_, { item }) => item.part_number,
          },
          {
            title: "Clave unidad",
            key: "ClaveUnidad",
            render: (_, { item }) => renderUnits(item),
          },
          {
            title: "Descripción",
            key: "Descripcion",
            render: (_, { item }) => renderDescription(item),
          },
          {
            title: "Valor unitario",
            key: "ValorUnitario",
            render: (_, { item }) => (
              <Flex justify="flex-end">
                <CfdiAmount
                  item={item}
                  amountType="unit_price"
                  includeCurrency={false}
                  style={amountsStyle}
                />
              </Flex>
            ),
          },
          {
            title: "Cantidad",
            key: "Cantidad",
            render: (_, { item }) => (
              <Flex justify="flex-end">{item.quantity.toFixed(2)}</Flex>
            ),
          },
          {
            title: "Importe",
            key: "Importe",
            render: (_, { item }) => (
              <Flex justify="flex-end">
                <CfdiAmount
                  item={item}
                  amountType="total"
                  includeCurrency={false}
                  style={amountsStyle}
                />
              </Flex>
            ),
          },
          {
            title: "Descuento",
            key: "Descuento",
            render: (_, { item }) => (
              <CfdiAmount
                item={item}
                amountType="discount"
                includeCurrency={false}
                style={amountsStyle}
              />
            ),
          },
        ];

    let dataSource = item.line_items?.map((item, i) => {
      return {
        key: i,
        item: item,
      };
    });

    return (
      <Table
        size="small"
        columns={columns}
        dataSource={dataSource}
        locale={{ emptyText: null }}
        pagination={false}
        expandable={{
          expandRowByClick: true,
          rowExpandable: (record) => !!record.item.taxes,
          expandedRowRender: (record) => renderTaxRow(record.item),
          showExpandColumn: !smallView,
        }}
      />
    );
  };

  const TableListPagos = () => {
    const columns = smallView
      ? [
          {
            title: "Documentos Relacionados",
            key: "id_cfdi_link",
            render: (_, { item }) => (
              <Flex vertical gap={0}>
                <RowItem
                  title={"Documento"}
                  text={
                    <Flex vertical gap={8} align="flex-start">
                      <CfdiLink
                        id_cfdi={item.cfdi_link.id_cfdi}
                        id_cfdi_url={id_cfdi_url}
                        setItem={setItem}
                      />
                    </Flex>
                  }
                  hideIfEmpty
                  vertical={width <= 480}
                />

                <RowItem
                  title={"Parcialidad"}
                  text={
                    <Flex vertical>
                      <Flex>{item.cfdi_link.partiality_number}</Flex>
                    </Flex>
                  }
                  hideIfEmpty
                />
                <RowItem
                  title={"Importe pagado"}
                  text={
                    <Flex vertical gap={5}>
                      <CfdiAmount
                        item={item.cfdi_link}
                        amountType="total"
                        includeCurrency={true}
                        style={amountsStyle}
                      />
                    </Flex>
                  }
                  indent
                  hideIfEmpty
                />
                <RowItem
                  title={"Fecha de pago"}
                  text={getFormattedUnixDate(item.date_payment_at)}
                  hideIfEmpty
                  vertical={miniWidth}
                />
                <RowItem
                  title={"Forma de pago"}
                  text={item.payment_type_description}
                  hideIfEmpty
                  vertical={miniWidth}
                />
              </Flex>
            ),
          },
        ]
      : [
          {
            title: "Documento Relacionado",
            key: "id_cfdi_link",
            render: (_, { item }) => (
              <Flex vertical gap={5} align="flex-start">
                <CfdiLink
                  id_cfdi={item.cfdi_link.id_cfdi}
                  id_cfdi_url={id_cfdi_url}
                  setItem={setItem}
                />
              </Flex>
            ),
          },
          {
            title: "Parcialidad",
            key: "partiality_number",
            render: (_, { item }) => (
              <Flex vertical gap={5} align="center">
                <Flex>{item.cfdi_link.partiality_number}</Flex>
              </Flex>
            ),
          },
          {
            title: "Importe pagado",
            key: "amount_paid",
            render: (_, { item }) => (
              <Flex vertical gap={5} align="flex-end">
                <CfdiAmount
                  item={item.cfdi_link}
                  amountType="total"
                  includeCurrency={true}
                  style={amountsStyle}
                />
              </Flex>
            ),
          },
          {
            title: "Fecha de pago",
            key: "payment_date",
            render: (_, { item }) => getFormattedUnixDate(item.date_payment_at),
          },
          {
            title: "Forma de pago",
            key: "payment_method",
            render: (_, { item }) => item.payment_type_description,
          },
        ];

    let dataSource = item.payments?.map((item, i) => {
      return {
        key: i,
        item: item,
      };
    });

    return (
      <Table
        size="small"
        columns={columns}
        dataSource={dataSource}
        locale={{ emptyText: null }}
        pagination={false}
      />
    );
  };

  return (
    <Modal
      title="Detalles del CFDI"
      open={isModalOpen}
      onCancel={handleCancel}
      width={1200}
      destroyOnClose={true}
      footer={null}
    >
      {item && item !== -1 ? (
        <Flex vertical gap={30}>
          <Flex vertical gap={15}>
            <HeaderInfo />
            <DetailInfo />
          </Flex>
          {item.line_items?.length > 0 ? (
            <>
              <TableLineItems />
              {item.cfdi_type === "P" && (
                <Flex vertical gap={10}>
                  <Text strong style={{ fontSize: "16px" }}>
                    Recepción de Pagos
                  </Text>
                  <TableListPagos />
                </Flex>
              )}
              {item.cfdi_type !== "P" && (
                <SectionLinkedCfdis
                  id_cfdi={id_cfdi}
                  smallView={smallView}
                  id_cfdi_url={id_cfdi_url}
                  setItem={setItem}
                />
              )}
            </>
          ) : item.cfdi_files_status === "canceled_no_xml" ? (
            <Flex align="center" justify="center" gap={8} vertical={smallView}>
              <FrownOutlined
                style={{
                  fontSize: "20px",
                  color: "#1C5676",
                }}
              />
              <Text style={{ fontSize: "15px", textAlign: "center" }}>
                No tenemos información sobre las partidas de este CFDI.
              </Text>
            </Flex>
          ) : (
            <Flex align="center" justify="center" gap={8} vertical>
              <Loading3QuartersOutlined
                spin
                style={{
                  fontSize: "20px",
                  color: "#1C5676",
                }}
              />
              <Text style={{ fontSize: "15px", textAlign: "center" }}>
                Las partidas de este CFDI se están cargando.
              </Text>
            </Flex>
          )}
        </Flex>
      ) : (
        <Flex
          align="center"
          justify="center"
          gap={8}
          vertical
          style={{ marginTop: "50px", marginBottom: "50px" }}
        >
          {item === -1 ? (
            <>
              <FileUnknownOutlined
                style={{
                  fontSize: "80px",
                  color: "#1C5676",
                }}
              />
              <Text style={{ fontSize: "15px", textAlign: "center" }}>
                El CFDI no fue encontrado.
              </Text>
              <Button
                type="primary"
                onClick={() => handleCancel()}
                style={{ marginTop: "20px" }}
              >
                Cerrar
              </Button>
            </>
          ) : (
            <>
              <Loading3QuartersOutlined
                spin
                style={{
                  fontSize: "20px",
                  color: "#1C5676",
                }}
              />
              <Text style={{ fontSize: "15px", textAlign: "center" }}>
                Cargando información del CFDI.
              </Text>
            </>
          )}
        </Flex>
      )}
    </Modal>
  );
}
